import { z } from "zod";
import { customerOrderContextV1Schema } from "./book-fulfillment-customer.js";
import { customerOperationsOpaqueIdSchema } from "./customer-operations-core.js";
import { ulidSchema } from "./schemas.js";
z.config({ jitless: true });
export const supportTicketCategorySchema = z.enum([
    "ACCOUNT_ACCESS",
    "GIFT_OR_VOUCHER",
    "BOOK_CREATION_OR_EDITING",
    "GENERATION_OR_EXPORT",
    "BILLING_OR_PAYMENT",
    "PRINT_OR_DELIVERY",
    "PRIVACY_OR_DATA_RIGHTS",
    "SAFETY_OR_ABUSE",
    "OTHER",
]);
export const supportTicketIdempotencyKeySchema = z
    .string()
    .trim()
    .min(16)
    .max(128)
    .regex(/^[A-Za-z0-9][A-Za-z0-9._:-]*$/u);
export const supportTicketMessageSchema = z.string().trim().min(1).max(4_000);
/** The book page a question was asked from, as the reader numbers it (Codex R21: "page 10"). */
export const supportTicketBookPageSchema = z.number().int().min(1).max(999);
/**
 * The step of making a book a question was asked from, in the customer's own words ("The words",
 * "Print review"), so the team sees where they were (Stage 2, F8). Shown to staff as written.
 */
export const supportTicketBookStepSchema = z
    .string()
    .trim()
    .min(1)
    .max(60)
    .regex(/^[\p{L}\p{N}][\p{L}\p{N} ’'&,.:()·-]*$/u);
/** Who wrote a request's latest message, so a list can say whose turn it is without opening it. */
export const supportTicketAuthorSchema = z.enum(["CUSTOMER", "SUPPORT"]);
/**
 * A customer can reopen a closed request by replying for this long after it closed (owner,
 * 2026-10-27). After that, something new starts a new request.
 */
export const SUPPORT_CUSTOMER_REOPEN_DAYS = 30;
const reopenWindowMs = SUPPORT_CUSTOMER_REOPEN_DAYS * 24 * 60 * 60_000;
/** When a request closed at `resolvedAt` stops taking a customer's reply. */
export function supportTicketReopenUntil(resolvedAt) {
    return new Date(Date.parse(resolvedAt) + reopenWindowMs).toISOString();
}
export const supportTicketIntakeCommandSchema = z
    .object({
    category: supportTicketCategorySchema,
    message: supportTicketMessageSchema,
    bookId: customerOperationsOpaqueIdSchema.optional(),
    // Only with a book: the page of that book the question came from.
    bookPage: supportTicketBookPageSchema.optional(),
    // Only with a book: the step of making it the question came from.
    bookStep: supportTicketBookStepSchema.optional(),
    // Only with a book: the printed copy the question is about. Attached to the request only
    // when the server finds it is this customer's own order of that book (G6); never trusted.
    physicalOrderId: ulidSchema.optional(),
    idempotencyKey: supportTicketIdempotencyKeySchema,
})
    .strict();
/**
 * The order a request is about, as the server verified it (G6): the customer's own order of this
 * very book, with the edition and print files it was made from. Never a reference the customer
 * typed; read through the engine's authorized order-context port.
 */
export const supportVerifiedOrderContextSchema = customerOrderContextV1Schema
    .extend({ physicalOrderId: ulidSchema, verifiedAt: z.string().datetime() })
    .strict();
export const supportBookMembershipProofSchema = z.discriminatedUnion("state", [
    z.object({ state: z.literal("NO_BOOK_SELECTED") }).strict(),
    z
        .object({
        state: z.literal("MEMBER_VERIFIED"),
        bookId: customerOperationsOpaqueIdSchema,
        customerId: customerOperationsOpaqueIdSchema,
        order: supportVerifiedOrderContextSchema.optional(),
    })
        .strict(),
]);
export const authenticatedSupportIdentitySchema = z
    .object({
    authenticated: z.literal(true),
    identitySource: z.literal("SERVER_DERIVED"),
    customerId: customerOperationsOpaqueIdSchema,
})
    .strict();
export const preparedSupportTicketIntakeSchema = supportTicketIntakeCommandSchema
    .omit({ idempotencyKey: true })
    .extend({
    customerId: customerOperationsOpaqueIdSchema,
    identitySource: z.literal("SERVER_DERIVED"),
    idempotencyKey: supportTicketIdempotencyKeySchema,
    orderContext: supportVerifiedOrderContextSchema.optional(),
})
    .strict();
export function prepareAuthenticatedSupportTicketIntake(input) {
    const command = supportTicketIntakeCommandSchema.parse(input.command);
    const identity = authenticatedSupportIdentitySchema.parse(input.identity);
    const membership = supportBookMembershipProofSchema.parse(input.membership);
    if (command.bookPage !== undefined && !command.bookId)
        throw new Error("SUPPORT_BOOK_PAGE_REQUIRES_BOOK");
    if (command.bookStep !== undefined && !command.bookId)
        throw new Error("SUPPORT_BOOK_STEP_REQUIRES_BOOK");
    if (command.physicalOrderId !== undefined && !command.bookId)
        throw new Error("SUPPORT_ORDER_REQUIRES_BOOK");
    if (command.bookId) {
        if (membership.state !== "MEMBER_VERIFIED" ||
            membership.bookId !== command.bookId ||
            membership.customerId !== identity.customerId)
            throw new Error("SUPPORT_BOOK_MEMBERSHIP_REQUIRED");
    }
    else if (membership.state !== "NO_BOOK_SELECTED") {
        throw new Error("SUPPORT_BOOK_MEMBERSHIP_UNEXPECTED");
    }
    // Only a verification of exactly the order asked about, for exactly this book, is attached.
    const order = membership.state === "MEMBER_VERIFIED" ? membership.order : undefined;
    if (order &&
        (order.physicalOrderId !== command.physicalOrderId || order.bookId !== command.bookId))
        throw new Error("SUPPORT_ORDER_CONTEXT_MISMATCH");
    return preparedSupportTicketIntakeSchema.parse({
        ...command,
        customerId: identity.customerId,
        identitySource: "SERVER_DERIVED",
        ...(order ? { orderContext: order } : {}),
    });
}
export const supportTicketCustomerStateSchema = z.enum([
    "RECEIVED",
    "IN_REVIEW",
    "WAITING_FOR_YOU",
    "RESOLVED",
]);
export const supportTicketReceiptSchema = z
    .object({
    ticketId: customerOperationsOpaqueIdSchema,
    state: z.literal("RECEIVED"),
    receivedAt: z.string().datetime(),
    acknowledgement: z.literal("INTAKE_RECORDED"),
    /** Present when an order was asked about: whether the server could attach it. */
    orderContext: z.enum(["ATTACHED", "NOT_ATTACHED"]).optional(),
})
    .strict();
export const supportTicketIntakeResponseSchema = z
    .object({
    data: supportTicketReceiptSchema,
    meta: z
        .object({
        stage: z.enum(["development", "staging", "public-sandbox", "production"]),
        test: z.boolean(),
        operationsEventState: z.literal("PENDING_DELIVERY"),
    })
        .strict(),
})
    .strict();
export const customerSupportTicketSummarySchema = z
    .object({
    ticketId: customerOperationsOpaqueIdSchema,
    category: supportTicketCategorySchema,
    state: supportTicketCustomerStateSchema,
    receivedAt: z.string().datetime(),
    /**
     * When the customer's view of the request last changed: a message either way, or its state.
     * Staff-only work (notes, who handles it, drafts, remedies) never moves it (2026-10-28).
     */
    updatedAt: z.string().datetime(),
    bookId: customerOperationsOpaqueIdSchema.optional(),
    bookPage: supportTicketBookPageSchema.optional(),
    /** The printed copy attached to this request, as the server verified it (G6). */
    physicalOrderId: ulidSchema.optional(),
    /**
     * The conversation's revision as the customer sees it, sent back as a reply's
     * `expectedVersion`. It moves with what `updatedAt` does and nothing else, so a teammate's
     * private work never turns a reply away. Not the request's internal staff revision.
     */
    version: z.number().int().positive(),
    /** Absent on requests from before 2026-10-27; the state then says whose turn it is. */
    lastMessageFrom: supportTicketAuthorSchema.optional(),
    /** A closed request takes a reply that reopens it until this time. */
    reopenUntil: z.string().datetime().optional(),
})
    .strict();
export const customerSupportTicketMessageSchema = z
    .object({
    messageId: customerOperationsOpaqueIdSchema,
    authorType: z.enum(["CUSTOMER", "SUPPORT"]),
    message: supportTicketMessageSchema,
    createdAt: z.string().datetime(),
})
    .strict();
export const customerSupportTicketDetailSchema = customerSupportTicketSummarySchema
    .extend({
    initialMessage: supportTicketMessageSchema,
    messages: z.array(customerSupportTicketMessageSchema).max(200),
})
    .strict();
export const customerSupportTicketListResponseSchema = z
    .object({ data: z.array(customerSupportTicketSummarySchema).max(100) })
    .strict();
export const customerSupportTicketDetailResponseSchema = z
    .object({ data: customerSupportTicketDetailSchema })
    .strict();
export const customerSupportTicketReplyCommandSchema = z
    .object({
    message: supportTicketMessageSchema,
    /** The `version` the customer last read: refused only when what they can see changed. */
    expectedVersion: z.number().int().positive(),
})
    .strict();
export const customerSupportTicketReplyResponseSchema = z
    .object({
    data: z
        .object({
        ticketId: customerOperationsOpaqueIdSchema,
        // A message before our first reply leaves the request where it is in the queue.
        state: z.enum(["RECEIVED", "IN_REVIEW"]),
        /** The conversation's revision after this reply, for the next one. */
        version: z.number().int().positive(),
        message: customerSupportTicketMessageSchema,
        /** The reply reopened a closed request (absent from servers before 2026-10-27). */
        reopened: z.boolean().optional(),
    })
        .strict(),
})
    .strict();
export function buildSupportTicketReceipt(input) {
    return supportTicketReceiptSchema.parse({
        ...input,
        state: "RECEIVED",
        acknowledgement: "INTAKE_RECORDED",
    });
}
export const supportTicketLifecycleSchema = z
    .object({
    ticketId: customerOperationsOpaqueIdSchema,
    state: supportTicketCustomerStateSchema,
    receivedAt: z.string().datetime(),
    assignedOperatorId: z.string().trim().min(1).max(128).optional(),
    reviewStartedAt: z.string().datetime().optional(),
    resolvedAt: z.string().datetime().optional(),
    lastResolvedAt: z.string().datetime().optional(),
    reopenedAt: z.string().datetime().optional(),
    version: z.number().int().positive(),
})
    .strict()
    .superRefine((ticket, context) => {
    if (ticket.state === "RECEIVED" &&
        (ticket.reviewStartedAt || ticket.resolvedAt || ticket.lastResolvedAt || ticket.reopenedAt))
        context.addIssue({
            code: "custom",
            path: ["state"],
            message: "A received ticket has not entered review or resolution.",
        });
    for (const field of [
        "reviewStartedAt",
        "resolvedAt",
        "lastResolvedAt",
        "reopenedAt",
    ]) {
        const value = ticket[field];
        if (value && Date.parse(value) < Date.parse(ticket.receivedAt))
            context.addIssue({
                code: "custom",
                path: [field],
                message: `${field} cannot precede receipt.`,
            });
    }
    if (["IN_REVIEW", "WAITING_FOR_YOU"].includes(ticket.state)) {
        if (!ticket.assignedOperatorId)
            context.addIssue({
                code: "custom",
                path: ["assignedOperatorId"],
                message: "Review states require an assigned operator.",
            });
        if (!ticket.reviewStartedAt)
            context.addIssue({
                code: "custom",
                path: ["reviewStartedAt"],
                message: "Review states require an explicit review start.",
            });
        if (ticket.resolvedAt)
            context.addIssue({
                code: "custom",
                path: ["resolvedAt"],
                message: "An open ticket cannot have a resolution time.",
            });
    }
    if (ticket.state === "RESOLVED") {
        if (!ticket.assignedOperatorId || !ticket.reviewStartedAt || !ticket.resolvedAt)
            context.addIssue({
                code: "custom",
                path: ["state"],
                message: "A resolved ticket requires assignment, review, and resolution evidence.",
            });
        if (ticket.reviewStartedAt &&
            ticket.resolvedAt &&
            Date.parse(ticket.resolvedAt) < Date.parse(ticket.reviewStartedAt))
            context.addIssue({
                code: "custom",
                path: ["resolvedAt"],
                message: "Resolution cannot precede review.",
            });
    }
    if ((ticket.lastResolvedAt === undefined) !== (ticket.reopenedAt === undefined))
        context.addIssue({
            code: "custom",
            path: ["reopenedAt"],
            message: "Reopen evidence requires the preceding resolution and reopen time.",
        });
    if (ticket.lastResolvedAt &&
        ticket.reopenedAt &&
        Date.parse(ticket.reopenedAt) < Date.parse(ticket.lastResolvedAt))
        context.addIssue({
            code: "custom",
            path: ["reopenedAt"],
            message: "Reopen cannot precede the prior resolution.",
        });
    if (ticket.reopenedAt &&
        ticket.reviewStartedAt &&
        Date.parse(ticket.reviewStartedAt) < Date.parse(ticket.reopenedAt))
        context.addIssue({
            code: "custom",
            path: ["reviewStartedAt"],
            message: "Reopened review cannot precede reopen.",
        });
});
export const supportTicketLifecycleActionSchema = z.discriminatedUnion("type", [
    z
        .object({
        type: z.literal("ASSIGN"),
        operatorId: z.string().trim().min(1).max(128),
    })
        .strict(),
    z
        .object({
        type: z.literal("START_REVIEW"),
        operatorId: z.string().trim().min(1).max(128),
        occurredAt: z.string().datetime(),
    })
        .strict(),
    z.object({ type: z.literal("WAIT_FOR_CUSTOMER") }).strict(),
    z.object({ type: z.literal("RECORD_RESPONSE") }).strict(),
    z
        .object({
        type: z.literal("CUSTOMER_REOPEN"),
        occurredAt: z.string().datetime(),
    })
        .strict(),
    z
        .object({
        type: z.literal("RESOLVE"),
        occurredAt: z.string().datetime(),
    })
        .strict(),
    z
        .object({
        type: z.literal("REOPEN_REVIEW"),
        operatorId: z.string().trim().min(1).max(128),
        occurredAt: z.string().datetime(),
    })
        .strict(),
]);
export function transitionSupportTicketLifecycle(currentValue, actionValue) {
    const current = supportTicketLifecycleSchema.parse(currentValue);
    const action = supportTicketLifecycleActionSchema.parse(actionValue);
    const nextVersion = current.version + 1;
    switch (action.type) {
        case "ASSIGN":
            if (current.state === "RESOLVED")
                throw new Error("SUPPORT_TICKET_ALREADY_RESOLVED");
            return supportTicketLifecycleSchema.parse({
                ...current,
                assignedOperatorId: action.operatorId,
                version: nextVersion,
            });
        case "START_REVIEW":
            if (current.state !== "RECEIVED")
                throw new Error("SUPPORT_REVIEW_START_INVALID");
            if (current.assignedOperatorId !== action.operatorId)
                throw new Error("SUPPORT_REVIEW_REQUIRES_ASSIGNEE");
            if (Date.parse(action.occurredAt) < Date.parse(current.receivedAt))
                throw new Error("SUPPORT_REVIEW_PRECEDES_RECEIPT");
            return supportTicketLifecycleSchema.parse({
                ...current,
                state: "IN_REVIEW",
                reviewStartedAt: action.occurredAt,
                version: nextVersion,
            });
        case "WAIT_FOR_CUSTOMER":
            if (current.state !== "IN_REVIEW")
                throw new Error("SUPPORT_WAIT_INVALID");
            return supportTicketLifecycleSchema.parse({
                ...current,
                state: "WAITING_FOR_YOU",
                version: nextVersion,
            });
        case "RECORD_RESPONSE":
            // Before review starts a customer's added details leave the request waiting where it is.
            if (current.state === "RECEIVED")
                return supportTicketLifecycleSchema.parse({ ...current, version: nextVersion });
            if (!["IN_REVIEW", "WAITING_FOR_YOU"].includes(current.state))
                throw new Error("SUPPORT_RESPONSE_INVALID");
            return supportTicketLifecycleSchema.parse({
                ...current,
                state: "IN_REVIEW",
                version: nextVersion,
            });
        case "CUSTOMER_REOPEN":
            // The customer's reply on a closed request brings it back to whoever closed it.
            if (current.state !== "RESOLVED")
                throw new Error("SUPPORT_REOPEN_INVALID");
            if (!current.resolvedAt || Date.parse(action.occurredAt) < Date.parse(current.resolvedAt))
                throw new Error("SUPPORT_REOPEN_PRECEDES_RESOLUTION");
            if (Date.parse(action.occurredAt) > Date.parse(supportTicketReopenUntil(current.resolvedAt)))
                throw new Error("SUPPORT_REOPEN_WINDOW_CLOSED");
            return supportTicketLifecycleSchema.parse({
                ...current,
                state: "IN_REVIEW",
                reviewStartedAt: action.occurredAt,
                lastResolvedAt: current.resolvedAt,
                reopenedAt: action.occurredAt,
                resolvedAt: undefined,
                version: nextVersion,
            });
        case "RESOLVE":
            if (!["IN_REVIEW", "WAITING_FOR_YOU"].includes(current.state))
                throw new Error("SUPPORT_RESOLUTION_INVALID");
            if (!current.reviewStartedAt ||
                Date.parse(action.occurredAt) < Date.parse(current.reviewStartedAt))
                throw new Error("SUPPORT_RESOLUTION_PRECEDES_REVIEW");
            return supportTicketLifecycleSchema.parse({
                ...current,
                state: "RESOLVED",
                resolvedAt: action.occurredAt,
                version: nextVersion,
            });
        case "REOPEN_REVIEW":
            if (current.state !== "RESOLVED")
                throw new Error("SUPPORT_REOPEN_INVALID");
            if (!current.resolvedAt || Date.parse(action.occurredAt) < Date.parse(current.resolvedAt))
                throw new Error("SUPPORT_REOPEN_PRECEDES_RESOLUTION");
            return supportTicketLifecycleSchema.parse({
                ...current,
                state: "IN_REVIEW",
                assignedOperatorId: action.operatorId,
                reviewStartedAt: action.occurredAt,
                lastResolvedAt: current.resolvedAt,
                reopenedAt: action.occurredAt,
                resolvedAt: undefined,
                version: nextVersion,
            });
    }
}
const supportIdempotencyRequestSchema = preparedSupportTicketIntakeSchema.omit({
    idempotencyKey: true,
});
/** Private support storage only. This record contains the support body and is never an event. */
export const privateSupportIdempotencyRecordSchema = z
    .object({
    idempotencyKey: supportTicketIdempotencyKeySchema,
    request: supportIdempotencyRequestSchema,
    receipt: supportTicketReceiptSchema,
})
    .strict();
export const supportIdempotencyDecisionSchema = z.discriminatedUnion("decision", [
    z.object({ decision: z.literal("NEW") }).strict(),
    z
        .object({
        decision: z.literal("REPLAY"),
        receipt: supportTicketReceiptSchema,
    })
        .strict(),
    z
        .object({
        decision: z.literal("CONFLICT"),
        reasonCode: z.literal("IDEMPOTENCY_KEY_REUSED"),
    })
        .strict(),
]);
export function decideSupportTicketIdempotency(input) {
    const request = preparedSupportTicketIntakeSchema.parse(input.request);
    if (input.exactKeyRecord === undefined)
        return { decision: "NEW" };
    const existing = privateSupportIdempotencyRecordSchema.parse(input.exactKeyRecord);
    if (existing.idempotencyKey !== request.idempotencyKey)
        throw new Error("SUPPORT_IDEMPOTENCY_EXACT_LOOKUP_KEY_MISMATCH");
    const requested = supportIdempotencyRequestSchema.parse({
        category: request.category,
        message: request.message,
        bookId: request.bookId,
        bookPage: request.bookPage,
        bookStep: request.bookStep,
        physicalOrderId: request.physicalOrderId,
        customerId: request.customerId,
        identitySource: request.identitySource,
    });
    // The order asked about is part of the request; whether it could be attached is the receipt's.
    if (existing.request.category === requested.category &&
        existing.request.message === requested.message &&
        existing.request.bookId === requested.bookId &&
        existing.request.bookPage === requested.bookPage &&
        existing.request.bookStep === requested.bookStep &&
        existing.request.physicalOrderId === requested.physicalOrderId &&
        existing.request.customerId === requested.customerId &&
        existing.request.identitySource === requested.identitySource)
        return supportIdempotencyDecisionSchema.parse({
            decision: "REPLAY",
            receipt: existing.receipt,
        });
    return { decision: "CONFLICT", reasonCode: "IDEMPOTENCY_KEY_REUSED" };
}
//# sourceMappingURL=customer-operations-support.js.map